import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { selectUserToken } from "../../features/user";
import cidadeApi from "../../utility/api/cidadeApi";
import estadoApi from "../../utility/api/estadoApi";
import paisApi from "../../utility/api/paisApi";
import useQuery from "../../utility/useQuery";
import Swal from "sweetalert2";
import Button from "../../components/Button";
import Modal from "../../components/Modal";
import Input from "../../components/Input";
import ButtonClasses from "../../components/Button/typing";
import CancelarButton from "../../components/CancelarButton";
import Dropdown from "../../components/Dropdown";
import { NotAllowed, permissoes, useAthorize } from "../../hooks/useAthorize";

const EdicaoCidades = ({ setIsVisible, onFinished }) => {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    getValues,
    setValue,
    formState: { errors },
  } = useForm();

  let isAllowed = useAthorize(
    permissoes.localizacaoConsulta,
    permissoes.localizacaoFull
  );

  const query = useQuery();
  let cidadeId = parseInt(query.get("cidadeId") ?? 0);

  const token = useSelector(selectUserToken);
  const [paises, setPaises] = useState([]);
  const [estados, setEstados] = useState([]);

  const onSubmit = useCallback(
    async ({ nome, estadoId, paisId }) => {
      let data = {
        cidadeId,
        nome,
        estadoId,
        paisId,
      };

      let result = await cidadeApi.atualizarCidade(data, token);

      if (result.hasError) {
        Swal.fire({
          title: "Erro!",
          text: "Algo deu errado",
          icon: "error",
        });
        return;
      }

      if (result.data.length === 0) {
        reset(data);
        onFinished?.();

        Swal.fire({
          title: "Sucesso!",
          text: "Cadastrado com sucesso",
          icon: "success",
        });
        return;
      }

      Swal.fire({
        title: "Erro!",
        text: result.data,
        icon: "error",
      });
    },
    [onFinished, cidadeId, reset, token]
  );

  const fetchCidade = useCallback(async () => {
    var result = await cidadeApi.buscarCidade(cidadeId, token);

    if (result.hasError) {
      Swal.fire({
        title: "Erro!",
        text: "Algo deu errado",
        icon: "error",
      });
      return;
    }

    reset(result.data);
  }, [cidadeId, reset, token]);

  const loadPaises = useCallback(async () => {
    var result = await paisApi.listarDropdown(null, token);

    if (result.hasError) {
      Swal.fire({
        title: "Erro!",
        text: "Algo deu errado",
        icon: "error",
      });
      return;
    }

    setPaises(result.data);
  }, [token]);

  const loadEstados = useCallback(
    async (getValues) => {
      var paisId = getValues("paisId");
      var result = await estadoApi.listarDropdown({ paisId }, token);

      if (result.hasError) {
        Swal.fire({
          title: "Erro!",
          text: "Algo deu errado",
          icon: "error",
        });
        return;
      }

      setEstados(result.data);
    },
    [token]
  );

  useState(() => {
    loadPaises();
    loadEstados(getValues);
    fetchCidade();
  }, []);

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name !== "paisId") return;
      loadEstados(getValues);
    });
    return () => subscription.unsubscribe();
  }, [getValues, loadEstados, watch]);

  var footer = (
    <>
      <CancelarButton
        watch={watch}
        reset={() =>
          Swal.fire({
            text: "Realmente deseja cancelar o cadastro?",
            showDenyButton: true,
            confirmButtonText: "Sim",
            denyButtonText: `Não`,
          }).then(async (result) => {
            if (result.isConfirmed) {
              reset();

              Swal.fire({
                text: "Cancelado com sucesso!",
                icon: "success",
              });
            }
          })
        }
        getValues={getValues}
        voltar={() => setIsVisible()}
      />
      <Button
        colorClass={ButtonClasses.DarkGreen}
        text="Salvar"
        onClick={handleSubmit(onSubmit)}
      />
    </>
  );

  if (!isAllowed) return <NotAllowed />;

  return (
    <Modal
      setModal={setIsVisible}
      title="Edição de Cidade"
      footer={footer}
      height="h-auto"
      heightBody="h-[64%] py-6"
    >
      <div className="w-full h-full flex justify-center items-center">
        <form onSubmit={handleSubmit(onSubmit)} className="w-[86%]">
          <div className="grid grid-cols-1 gap-8">
            <Dropdown
              name="paisId"
              defaultText="Selecione o país"
              register={register}
              setValue={setValue}
              watch={watch}
              getValues={getValues}
              options={paises}
              width={"w-full"}
              label="País"
              errors={errors}
              required
            />

            <Dropdown
              name="estadoId"
              defaultText="Selecione o estado"
              register={register}
              setValue={setValue}
              watch={watch}
              getValues={getValues}
              options={estados}
              width={"w-full"}
              label="Estado"
              errors={errors}
              required
              readonly={!Boolean(getValues("paisId"))}
            />

            <Input
              label="Cidade"
              name="nome"
              type="text"
              placeholder="Insira o nome da cidade"
              register={register}
              required
              errors={errors}
            />
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default EdicaoCidades;
